import type { 
  CourseQueryParams, 
  Course, 
  CreateCourseInput, 
  UpdateCourseInput, 
  User, 
  UpdateUserInput, 
  LoginResponse, 
  RefreshTokenResponse,
  ApiResponse,
  Enrollment,
  EnrollmentQueryParams,
  CreateEnrollmentInput,
  UpdateEnrollmentInput,
  Combo,
  ComboQueryParams,
  CreateComboInput,
  UpdateComboInput,
  CreateUserFromTelegramInput,
  DevLoginInput,
  DevLoginResponse,
  CheckoutSessionResponse,
  PaymentStatusResponse,
  AdminStats,
  TeacherStats,
  AppSettings,
  ClientUiConfig
} from '@/types/api'
import { DEFAULT_CLIENT_UI } from './client-ui'
import { mockStorage, STORAGE_KEYS } from './mock-storage'
import { shouldRequireAuth } from './auth-utils'

// Mock API 實現，由 ApiModeManager 控制是否使用

class MockApiClient {
  private checkAuth(): void {
    // Skip auth check if authentication is not required (development mode)
    if (!shouldRequireAuth()) {
      return
    }
    
    if (typeof window === 'undefined') return
    
    const token = localStorage.getItem('access_token')
    if (!token || token !== STORAGE_KEYS.TOKEN) {
      throw new Error('Unauthorized - Invalid or missing token')
    }
  }

  private async delay(): Promise<void> {
    const delayTime = Math.floor(Math.random() * 500) + 300
    return new Promise(resolve => setTimeout(resolve, delayTime))
  }

  // Authentication
  async telegramLogin(initData: string): Promise<LoginResponse> {
    console.log('Mock API: telegramLogin called with data:', {
      length: initData?.length,
      preview: initData?.substring(0, 100) + '...',
      isWidgetFormat: initData?.includes('id=') && initData?.includes('hash=')
    })
    await this.delay()
    
    // Mock authentication - any initData longer than 10 chars is valid
    if (!initData || initData.length < 10) {
      console.error('Mock API: Invalid Telegram data - too short')
      throw new Error('Invalid Telegram data')
    }
    
    // Parse Widget format if needed
    let userData = null
    if (initData.includes('id=') && initData.includes('hash=')) {
      console.log('Mock API: Detected Widget format data')
      const params = new URLSearchParams(initData)
      userData = {
        id: params.get('id'),
        first_name: params.get('first_name'),
        last_name: params.get('last_name'),
        username: params.get('username'),
        photo_url: params.get('photo_url'),
        auth_date: params.get('auth_date'),
        hash: params.get('hash')
      }
      console.log('Mock API: Parsed widget data:', userData)
    } else {
      console.log('Mock API: Detected Mini App format data')
    }
    
    const user = await mockStorage.getUserProfile()
    const token = STORAGE_KEYS.TOKEN
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
    
    console.log('Mock API: Login successful, user:', user)
    console.log('Mock API: Token:', token)
    
    // Store token in localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', token)
      console.log('Mock API: Token stored in localStorage')
    }
    
    return {
      token,
      user,
      expiresAt
    }
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    await this.delay()
    this.checkAuth()
    
    const token = STORAGE_KEYS.TOKEN
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    
    return { token, expiresAt }
  }

  async logout(): Promise<{ message: string }> {
    await this.delay()
    
    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token')
    }
    
    return { message: 'Logged out successfully' }
  }

  async devLogin(credentials: DevLoginInput): Promise<DevLoginResponse> {
    await this.delay()
    
    console.log('MockAPI: Dev login for', credentials)
    
    // Create or get dev user
    const devUser: User = {
      id: credentials.userId,
      firstName: credentials.userId.split('-')[1] || 'Dev',
      lastName: credentials.role === 'admin' ? 'Admin' : credentials.role === 'teacher' ? 'Teacher' : 'Student',
      username: credentials.userId,
      email: credentials.email,
      role: credentials.role,
      status: 'active',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    // Generate mock JWT token
    const token = `mock_jwt_${credentials.userId}_${Date.now()}`
    
    // Store the token for subsequent API calls
    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', token)
    }

    return {
      token,
      user: devUser,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
    }
  }

  // Courses
  async getCourses(params?: CourseQueryParams): Promise<ApiResponse<Course[]>> {
    await this.delay()
    this.checkAuth()
    
    const result = await mockStorage.getCoursesWithPagination(params)
    
    return {
      data: result.data,
      pagination: result.pagination
    }
  }

  async getCourse(id: string): Promise<Course> {
    await this.delay()
    this.checkAuth()
    
    const course = await mockStorage.getCourseById(id)
    if (!course) {
      throw new Error('Course not found')
    }
    
    return course
  }

  async createCourse(courseData: CreateCourseInput): Promise<Course> {
    await this.delay()
    this.checkAuth()
    
    // Validate required fields
    if (!courseData.title?.trim()) {
      throw new Error('Course title is required')
    }
    if (!courseData.description?.trim()) {
      throw new Error('Course description is required')
    }
    if (!courseData.content?.trim()) {
      throw new Error('Course content is required')
    }
    if (courseData.price < 0) {
      throw new Error('Course price must be non-negative')
    }
    if (courseData.maxStudents <= 0) {
      throw new Error('Max students must be greater than 0')
    }
    
    return mockStorage.createCourse(courseData)
  }

  async updateCourseStatus(id: string, statusUpdate: { status: Course['status'] }): Promise<Course> {
    return this.updateCourse(id, statusUpdate as UpdateCourseInput)
  }

  async updateCourse(id: string, courseData: UpdateCourseInput): Promise<Course> {
    await this.delay()
    this.checkAuth()
    
    // Validate fields if provided
    if (courseData.title !== undefined && !courseData.title.trim()) {
      throw new Error('Course title cannot be empty')
    }
    if (courseData.description !== undefined && !courseData.description.trim()) {
      throw new Error('Course description cannot be empty')
    }
    if (courseData.price !== undefined && courseData.price < 0) {
      throw new Error('Course price must be non-negative')
    }
    if (courseData.maxStudents !== undefined && courseData.maxStudents <= 0) {
      throw new Error('Max students must be greater than 0')
    }
    
    return mockStorage.updateCourse(id, courseData)
  }

  async deleteCourse(id: string): Promise<{ message: string }> {
    await this.delay()
    this.checkAuth()
    
    const success = await mockStorage.deleteCourse(id)
    if (!success) {
      throw new Error('Failed to delete course')
    }
    
    return { message: 'Course deleted successfully' }
  }

  // Users
  async getProfile(): Promise<User> {
    await this.delay()
    this.checkAuth()
    
    return mockStorage.getUserProfile()
  }

  async updateProfile(userData: UpdateUserInput): Promise<User> {
    await this.delay()
    this.checkAuth()
    
    // For mock, just return the current user with updated fields
    const currentUser = await mockStorage.getUserProfile()
    
    return {
      ...currentUser,
      ...userData,
      updatedAt: new Date().toISOString()
    }
  }

  async getUsers(params?: {
    search?: string
    role?: string
    status?: string
    page?: number
    limit?: number
  }): Promise<ApiResponse<User[]>> {
    await this.delay()
    this.checkAuth()
    
    const result = await mockStorage.getUsersWithPagination(params)
    
    return {
      data: result.data,
      pagination: result.pagination
    }
  }

  async getUser(id: string): Promise<User> {
    console.log(`[MockAPI] Getting user with ID: ${id}`)
    await this.delay()
    this.checkAuth()
    
    try {
      const user = await mockStorage.getUserByIdAsync(id)
      console.log(`[MockAPI] getUser response:`, user)
      return user
    } catch (error) {
      console.error(`[MockAPI] getUser failed for ID ${id}:`, error)
      throw error
    }
  }

  async createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
    await this.delay()
    this.checkAuth()
    
    // Validate required fields
    if (!userData.firstName?.trim()) {
      throw new Error('First name is required')
    }
    if (!userData.telegramId) {
      throw new Error('Telegram ID is required')
    }
    if (!userData.role) {
      throw new Error('User role is required')
    }
    
    return mockStorage.createUser(userData)
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    await this.delay()
    this.checkAuth()
    
    // Validate fields if provided
    if (userData.firstName !== undefined && !userData.firstName.trim()) {
      throw new Error('First name cannot be empty')
    }
    
    return mockStorage.updateUser(id, userData)
  }

  async deleteUser(id: string): Promise<{ message: string }> {
    await this.delay()
    this.checkAuth()
    
    const success = await mockStorage.deleteUser(id)
    if (!success) {
      throw new Error('Failed to delete user')
    }
    
    return { message: 'User deleted successfully' }
  }

  // Enrollment operations
  async getEnrollments(params?: EnrollmentQueryParams): Promise<ApiResponse<Enrollment[]>> {
    await this.delay()
    this.checkAuth()
    
    const enrollments = await mockStorage.getEnrollments(params)
    return enrollments
  }

  async getEnrollment(id: string): Promise<Enrollment> {
    await this.delay()
    this.checkAuth()
    
    const enrollment = await mockStorage.getEnrollment(id)
    if (!enrollment) {
      throw new Error('Enrollment not found')
    }
    return enrollment
  }

  async createEnrollment(data: CreateEnrollmentInput): Promise<Enrollment> {
    await this.delay()
    this.checkAuth()
    
    const newEnrollment = await mockStorage.createEnrollment(data)
    return newEnrollment
  }

  async updateEnrollmentStatus(id: string, statusUpdate: { status: Enrollment['status'] }): Promise<Enrollment> {
    return this.updateEnrollment(id, statusUpdate as UpdateEnrollmentInput)
  }

  async updateEnrollment(id: string, data: UpdateEnrollmentInput): Promise<Enrollment> {
    await this.delay()
    this.checkAuth()
    
    const updatedEnrollment = await mockStorage.updateEnrollment(id, data)
    if (!updatedEnrollment) {
      throw new Error('Enrollment not found')
    }
    return updatedEnrollment
  }

  async deleteEnrollment(id: string): Promise<{ message: string }> {
    await this.delay()
    this.checkAuth()
    
    const success = await mockStorage.deleteEnrollment(id)
    if (!success) {
      throw new Error('Failed to delete enrollment')
    }
    
    return { message: 'Enrollment deleted successfully' }
  }

  async createCheckoutSession(enrollmentId: string): Promise<CheckoutSessionResponse> {
    await this.delay()
    this.checkAuth()
    return {
      url: `#mock-checkout/${enrollmentId}`,
      sessionId: `cs_mock_${enrollmentId}`,
    }
  }

  async getClientUiConfig(): Promise<ClientUiConfig> {
    await this.delay()
    this.checkAuth()
    try {
      const stored = localStorage.getItem('mock_client_ui_config')
      if (stored) return JSON.parse(stored)
    } catch { /* fallthrough */ }
    return DEFAULT_CLIENT_UI
  }

  async updateClientUiConfig(config: ClientUiConfig): Promise<ClientUiConfig> {
    await this.delay()
    this.checkAuth()
    const next = { ...config, version: (config.version || 0) + 1 }
    localStorage.setItem('mock_client_ui_config', JSON.stringify(next))
    return next
  }

  async getAppSettings(): Promise<AppSettings> {
    await this.delay()
    this.checkAuth()
    const stored = localStorage.getItem('mock_real_payments_enabled')
    return { realPaymentsEnabled: stored === 'true' }
  }

  async updateAppSettings(settings: AppSettings): Promise<AppSettings> {
    await this.delay()
    this.checkAuth()
    localStorage.setItem('mock_real_payments_enabled', settings.realPaymentsEnabled ? 'true' : 'false')
    return settings
  }

  async getAdminStats(): Promise<AdminStats> {
    await this.delay()
    this.checkAuth()
    return {
      users: { total: 42, active: 40, teachers: 6, students: 35 },
      courses: { total: 12, approved: 8, pending: 2 },
      enrollments: { total: 57, pending: 5 },
      combos: { total: 9 },
      revenue: [{ currency: 'TWD', total: 128000 }],
    }
  }

  async getTeacherStats(): Promise<TeacherStats> {
    await this.delay()
    this.checkAuth()
    return {
      courses: { total: 4, approved: 3 },
      enrollments: { total: 21, pending: 2 },
      revenue: [{ currency: 'TWD', total: 45600 }],
    }
  }

  async getPaymentStatus(enrollmentId: string): Promise<PaymentStatusResponse> {
    await this.delay()
    this.checkAuth()
    return {
      enrollmentId,
      paymentStatus: 'paid',
      paymentAmount: 1000,
      paymentCurrency: 'TWD',
      paidAt: new Date().toISOString(),
    }
  }

  // Combo endpoints
  async getCombos(params?: ComboQueryParams): Promise<ApiResponse<Combo[]>> {
    await this.delay()
    this.checkAuth()
    
    return await mockStorage.getCombosWithPagination(params)
  }

  async getCombo(id: string): Promise<Combo> {
    await this.delay()
    this.checkAuth()
    
    const combo = await mockStorage.getComboById(id)
    if (!combo) {
      throw new Error('Combo not found')
    }
    
    return combo
  }

  async createCombo(comboData: CreateComboInput & { teacherId: string }): Promise<Combo> {
    await this.delay()
    this.checkAuth()
    
    return await mockStorage.createCombo(comboData)
  }

  async updateCombo(id: string, comboData: UpdateComboInput): Promise<Combo> {
    await this.delay()
    this.checkAuth()
    
    return await mockStorage.updateCombo(id, comboData)
  }

  async deleteCombo(id: string): Promise<{ message: string }> {
    await this.delay()
    this.checkAuth()
    
    const success = await mockStorage.deleteCombo(id)
    if (!success) {
      throw new Error('Failed to delete combo')
    }
    
    return { message: 'Combo deleted successfully' }
  }

  // Telegram user creation
  async createUserFromTelegram(userData: CreateUserFromTelegramInput): Promise<User> {
    await this.delay()
    
    const userCreateData = {
      telegramId: userData.telegramId,
      firstName: userData.firstName,
      lastName: userData.lastName,
      username: userData.username,
      photoUrl: userData.photoUrl,
      role: userData.role || 'student',
      status: userData.status || 'active',
      isActive: true
    }
    
    console.log('MockAPI: Creating user from Telegram data:', userCreateData)
    const createdUser = await mockStorage.createUser(userCreateData)
    console.log('MockAPI: User created successfully:', createdUser)
    
    return createdUser
  }

  async checkUserByTelegramId(telegramId: number | string): Promise<{ exists: boolean; user?: User }> {
    await this.delay()
    
    const users = await mockStorage.getUsers()
    const existingUser = users.find(u => u.telegramId?.toString() === telegramId.toString())
    
    return {
      exists: !!existingUser,
      user: existingUser
    }
  }

  // File upload (mock)
  async uploadFile(_file: File): Promise<{ url: string }> {
    await this.delay()
    this.checkAuth()
    
    // Simulate file upload with a mock URL
    const mockUrl = `https://picsum.photos/400/300?random=${Date.now()}`
    
    return { url: mockUrl }
  }
}

export const mockApiClient = new MockApiClient()